/**
 * Hindi digit ordinals (1–30+): 1ला, 2रा, 3रा, 4था, 5वाँ, 6ठा, 7वाँ … 30वाँ.
 * Oblique (में/से/के): 1ले, 2रे, 3रे, 4थे, 5वें, 6ठे, 7वें … 12वें.
 * Use for bhava, rashi, nakshatra step counts, and any “Nth” label in Devanagari UI.
 */

/** @type {Record<number, string>} */
const ORDINAL_SUFFIX = {
  1: 'ला',
  2: 'रा',
  3: 'रा',
  4: 'था',
  5: 'वाँ',
  6: 'ठा',
};

/** @type {Record<number, string>} */
const ORDINAL_SUFFIX_OBLIQUE = {
  1: 'ले',
  2: 'रे',
  3: 'रे',
  4: 'थे',
  5: 'वें',
  6: 'ठे',
};

const DEFAULT_SUFFIX = 'वाँ';
const DEFAULT_SUFFIX_OBLIQUE = 'वें';

/**
 * @param {number} n 1–30+ (values above 30 use …वाँ)
 * @returns {string} e.g. "7वाँ", "1ला", "6ठा"
 */
export function hindiDigitOrdinal(n) {
  const num = Math.round(Number(n));
  if (!Number.isFinite(num) || num < 1) return String(n);
  const suffix = ORDINAL_SUFFIX[num] ?? DEFAULT_SUFFIX;
  return `${num}${suffix}`;
}

/**
 * Oblique form for “X में/से/के” (e.g. 6ठe भाव में, 5वें के स्वामी).
 * @param {number} n 1–30+
 * @returns {string}
 */
export function hindiDigitOrdinalOblique(n) {
  const num = Math.round(Number(n));
  if (!Number.isFinite(num) || num < 1) return String(n);
  const suffix = ORDINAL_SUFFIX_OBLIQUE[num] ?? DEFAULT_SUFFIX_OBLIQUE;
  return `${num}${suffix}`;
}

/** Alias for house/sign nominative ordinals. */
export const houseOrdinalDigitHi = hindiDigitOrdinal;

/** Alias for house/sign oblique ordinals. */
export const houseOrdinalDigitObliqueHi = hindiDigitOrdinalOblique;

/**
 * @param {number} house 1–12+
 * @returns {string} e.g. "7वाँ भाव"
 */
export function hindiBhavaOrdinal(house) {
  return `${hindiDigitOrdinal(house)} भाव`;
}

/** Alias for sign / rashi position labels (same suffix rules as bhava). */
export const hindiRashiOrdinal = hindiDigitOrdinal;

/**
 * @param {number} stepCount how many steps (≥1)
 * @returns {string} e.g. "1ला → 2रा → 3रा"
 */
export function hindiDigitOrdinalChain(stepCount) {
  const n = Math.max(0, Math.round(Number(stepCount)));
  if (n < 1) return '';
  return Array.from({ length: n }, (_, i) => hindiDigitOrdinal(i + 1)).join(' → ');
}

/**
 * @param {number[]} numbers
 * @returns {string}
 */
export function hindiDigitOrdinalJoin(numbers, separator = ' → ') {
  if (!Array.isArray(numbers) || numbers.length === 0) return '';
  return numbers.map((n) => hindiDigitOrdinal(n)).join(separator);
}
